import { useEffect, useRef } from 'react';
import { useSocketIO } from '../providers/SocketIOProvider';
import type { OrderStatusChangedEvent } from '../providers/SocketIOProvider';

/**
 * Call `onChange` when the backend reports a status change for this order (any source:
 * admin or app actions, courier webhooks, cancellations, the Loop robo courier). The
 * event arrives on the store's admin room (`admin:<storeId>`).
 *
 * Joins the store room but never leaves it: the room is shared with the app-wide
 * new-order alerts (OrderNotificationProvider) and the picking hook, and the server's
 * unsubscribe is not reference-counted — leaving here would silence them. Joining again
 * is harmless, and it re-joins after a reconnect.
 */
export function useOrderStatusLive(
  storeId: string | null | undefined,
  orderId: string | null | undefined,
  onChange: (event: OrderStatusChangedEvent) => void,
): void {
  const { isConnected, subscribeStore, onOrderStatusChanged } = useSocketIO();
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => {
    if (storeId && isConnected) subscribeStore(storeId);
  }, [storeId, isConnected, subscribeStore]);

  useEffect(() => {
    if (!orderId) return undefined;
    return onOrderStatusChanged((event) => {
      if (event.orderId === orderId) latest.current(event);
    });
  }, [orderId, onOrderStatusChanged]);
}
